import type {JSX} from 'react';
import React from 'react';

import Spinner from '@/assets/svg/Spinner';
// ButtonColorScheme is now exported from variants.ts
import type {ButtonColorScheme} from '@/constants/variants';
import {baseClasses, colorVariants} from '@/constants/variants';

export type ButtonProps = {
  children: JSX.Element | JSX.Element[] | string | React.ReactNode;
  variant?: 'solid' | 'outline' | 'unStyled' | 'ghost';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  colorScheme?: ButtonColorScheme;
  isLoading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  dataCy?: string;
  className?: string;
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  /**
   * Defaults to `'button'`, NOT the DOM's `'submit'`.
   *
   * A `<button>` with no type submits its enclosing form. That made every
   * Cancel button in a modal save the very edit it was meant to discard: 16
   * call sites across this package rendered `<Button onClick={onClose}>Cancel`
   * inside a `<form onSubmit={handleSave}>`, and clicking Cancel fired both.
   *
   * Safe to default because no form in this package relies on the implicit
   * submit: every submitting button names `type="submit"` itself (the currency
   * settings form's submit button sits outside its `</form>` and links back
   * with `form="currency-settings-form"`). A submit button must therefore keep
   * saying so explicitly, which is the convention anyway.
   */
  type?: 'button' | 'submit' | 'reset';
  // Open-ended so callers can pass through any <button> DOM attribute (plus the
  // `testId` this component reads below); `unknown` forwards through the spread.
  [rest: string]: unknown;
};

function Button({
  isLoading = false,
  disabled = false,
  loadingText,
  colorScheme = 'cyan',
  children,
  onClick,
  className = '',
  leftIcon,
  rightIcon,
  variant = 'solid',
  dataCy,
  // Destructured rather than left to ride along in `rest`, so the default
  // below actually applies; `rest` is spread last and would otherwise
  // reintroduce an undefined `type` and hand the button back to the DOM
  // default.
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  // Runtime validation for colorScheme
  const validColorSchemes = Object.keys(colorVariants);
  let safeColorScheme = colorScheme;
  if (!colorScheme || !colorVariants[colorScheme]) {
    if (!import.meta.env.PROD) {
      console.warn(
        `[Button] Invalid colorScheme '${colorScheme}'. Falling back to 'cyan'. Valid options: [${validColorSchemes.join(', ')}].`,
      );
    }
    safeColorScheme = 'cyan';
  }

  // Runtime validation for variant
  const validVariants = ['solid', 'outline', 'unStyled', 'ghost'];
  let safeVariant = variant;
  if (!variant || !validVariants.includes(variant)) {
    if (!import.meta.env.PROD) {
      console.warn(
        `[Button] Invalid variant '${variant}'. Falling back to 'solid'. Valid options: [${validVariants.join(', ')}].`,
      );
    }
    safeVariant = 'solid';
  }

  let variantClasses;
  if (disabled) {
    variantClasses = baseClasses['disabled'];
  } else if (safeVariant === 'solid' || safeVariant === 'outline') {
    variantClasses = colorVariants[safeColorScheme][safeVariant];
  } else {
    variantClasses = baseClasses[safeVariant];
  }

  return (
    <button
      data-cy={dataCy ? `${dataCy}-button` : undefined}
      className={`rounded-lg font-inherit ${variantClasses} ${className} ${disabled ? 'cursor-not-allowed' : ''}`}
      disabled={isLoading || disabled}
      onClick={onClick}
      type={type}
      {...(typeof rest.testId === 'string' &&
        rest.testId && {
          'data-testid': rest.testId,
          id: rest.testId,
        })}
      {...rest}
    >
      {
        <div className="flex justify-center items-center">
          {isLoading ? (
            <>
              <Spinner size={5} /> {loadingText}
            </>
          ) : (
            <>
              {leftIcon && <span className="mr-2">{leftIcon}</span>}
              {children}
              {rightIcon && <span className="ml-2">{rightIcon}</span>}
            </>
          )}
        </div>
      }
    </button>
  );
}

export default Button;
